// Where to send someone after they sign in.
//
// RequireAuth stashes the location a signed-out visitor tried to open
// (`state.from`), and PublicOnly sends them back there after login. That
// location comes from the URL they clicked, so it is attacker-controlled:
// `https://mono…//evil.example.com/x` arrives with pathname
// `//evil.example.com/x`, which a browser reads as another origin. React
// Router ≥7.18 refuses those itself (GHSA-wrjc-x8rr-h8h6 was a backslash
// bypass of its earlier check); this is the app's own check, so a router
// regression can't turn the login page into an open redirect (C7 in
// projects/dependency-hygiene/report.md).
//
// Only a same-origin app path is accepted: one leading "/", never "//",
// no backslash (URL parsers read "\" as "/"), no control characters (they
// strip tab, newline and carriage return, so "/\t/evil" becomes "//evil";
// DEL and NUL are refused with the rest), and the same
// rules after percent-decoding. Anything else returns null and the caller
// uses its default route. Search and hash are kept, so a deep link like
// /tasks/board?task=12#comments survives the trip through /login.

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

function isAppPath(path) {
  return (
    typeof path === "string" &&
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.includes("\\") &&
    !CONTROL_CHARS.test(path)
  );
}

/**
 * @param {unknown} from - a router location ({ pathname, search, hash })
 * @returns {string | null} the path to return to, or null if unsafe/absent
 */
export function safeReturnPath(from) {
  const { pathname, search = "", hash = "" } = /** @type {any} */ (from ?? {});
  if (!isAppPath(pathname)) return null;

  let decoded;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    return null; // malformed escape: not a path the app produced
  }
  if (!isAppPath(decoded)) return null;

  if (typeof search !== "string" || (search && !search.startsWith("?"))) {
    return null;
  }
  if (typeof hash !== "string" || (hash && !hash.startsWith("#"))) {
    return null;
  }

  return pathname + search + hash;
}
